/**
 * Converts an HTML fragment to its plain-text content using the DOM.
 * The input is first assigned to a detached `<textarea>` so HTML entities
 * (e.g. `&amp;`, `&nbsp;`) are decoded, then the decoded markup is parsed by a
 * detached `<div>` whose `textContent`/`innerText` strips all tags. Returns an
 * empty string when the input has no text content.
 */
export function htmlToPlainText(html: string): string {
  const textarea = document.createElement("textarea");
  textarea.innerHTML = html;
  const temporalDivElement = document.createElement("div");
  temporalDivElement.innerHTML = textarea.value;
  return temporalDivElement.textContent || temporalDivElement.innerText || "";
}

/**
 * Counts the words in the plain-text content of an HTML fragment.
 * The HTML is converted with {@link htmlToPlainText}, trimmed, and split on
 * whitespace runs followed by a word boundary. Returns 0 for empty or
 * whitespace-only content (e.g. a lone `&nbsp;`).
 */
export function wordCount(html: string): number {
  const trimmed = htmlToPlainText(html).trim();
  return trimmed ? trimmed.split(/\s+\b/).length : 0;
}

/**
 * Counts the characters in the trimmed plain-text content of an HTML fragment
 * (converted with {@link htmlToPlainText}). Returns 0 for empty content.
 */
export function characterCount(html: string): number {
  return (htmlToPlainText(html).length && htmlToPlainText(html).trim().length) || 0;
}
